import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export interface KitWithPhoto {
  id: string;
  club: string;
  season: string;
  brand: string;
  kit_type: string;
  condition: string | null;
  estimated_value: number | null;
  purchase_currency: string | null;
  tags: string[] | null;
  photoUrl?: string;
  likesCount: number;
  commentsCount: number;
  ownerName?: string;
  ownerAvatar?: string;
  user_id: string;
  isLikedByMe: boolean;
  created_at: string;
}

const PAGE_SIZE = 10;
const REQUEST_TIMEOUT_MS = 8000;
const FEED_CACHE_PREFIX = "index:feed:v2:";

const getCacheKey = (userId: string | null | undefined) =>
  `${FEED_CACHE_PREFIX}${userId ?? "guest"}`;

const readFeedCache = (userId: string | null | undefined) => {
  try {
    const raw = sessionStorage.getItem(getCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.kits)) return null;
    return parsed as { kits: KitWithPhoto[]; page: number; hasMore: boolean; offset?: number };
  } catch {
    return null;
  }
};

const writeFeedCache = (
  userId: string | null | undefined,
  kits: KitWithPhoto[],
  page: number,
  hasMore: boolean,
  offset?: number,
) => {
  try {
    sessionStorage.setItem(getCacheKey(userId), JSON.stringify({ kits, page, hasMore, offset }));
  } catch {
    /* ignore quota */
  }
};

const fetchOwnerProfiles = async (ownerIds: string[]) => {
  const withSuspension = await supabase
    .from("profiles")
    .select("user_id, username, avatar_url, is_suspended")
    .in("user_id", ownerIds);
  if (!withSuspension.error) return withSuspension;
  return supabase.from("profiles").select("user_id, username, avatar_url").in("user_id", ownerIds);
};

const withTimeout = async <T,>(promise: PromiseLike<T>, fallback: T, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T> =>
  Promise.race([
    Promise.resolve(promise),
    new Promise<T>((resolve) => {
      window.setTimeout(() => resolve(fallback), timeoutMs);
    }),
  ]);

const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

// Zapytanie krytyczne: zamiast po cichu zwracać pustą listę, ponawia próbę,
// a gdy dalej się nie uda — rzuca błąd (UI pokaże "Spróbuj ponownie").
const runCritical = async <T extends { data: any; error: any }>(
  run: () => PromiseLike<T>,
  attempts = 3,
  timeoutMs = 10000,
): Promise<T> => {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = (await Promise.race([
        Promise.resolve(run()),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error("request timeout")), timeoutMs);
        }),
      ])) as T;
      if (res?.error) throw res.error;
      return res;
    } catch (e) {
      lastError = e;
      if (i < attempts - 1) await sleep(500 * (i + 1));
    }
  }
  throw lastError instanceof Error ? lastError : new Error("request failed");
};


const enrichKits = (
  kits: any[],
  photoMap: Map<string, string>,
  profileMap: Map<string, any>,
  likesMap: Map<string, number>,
  commentsMap: Map<string, number>,
  myLikes: Set<string>,
): KitWithPhoto[] =>
  kits.map((kit) => {
    const profile = profileMap.get(kit.user_id);
    return {
      ...kit,
      photoUrl: photoMap.get(kit.id),
      likesCount: likesMap.get(kit.id) || 0,
      commentsCount: commentsMap.get(kit.id) || 0,
      ownerName: profile?.username || undefined,
      ownerAvatar: profile?.avatar_url,
      isLikedByMe: myLikes.has(kit.id),
    };
  });

// Ranking z CAŁEJ bazy: publiczne koszulki, bez "na sprzedaż", bez kont zawieszonych.
const fetchTopKitsGlobal = async (): Promise<KitWithPhoto[] | null> => {
  const rankRes: any = await withTimeout(
    supabase.rpc("get_top_kit_ids" as any, { _limit: 10 }) as any,
    { data: null, error: new Error("top kits timeout") } as any,
  );
  if (rankRes.error || !Array.isArray(rankRes.data) || rankRes.data.length === 0) return null;

  const likesMap = new Map<string, number>();
  const order: string[] = [];
  rankRes.data.forEach((row: any) => {
    likesMap.set(row.kit_id, Number(row.likes_count) || 0);
    order.push(row.kit_id);
  });

  const kitsRes: any = await withTimeout(
    supabase.from("kits").select("*").in("id", order),
    { data: [], error: new Error("top kits fetch timeout") } as any,
  );
  const kits = kitsRes.data || [];
  if (kits.length === 0) return null;

  const ownerIds = Array.from(new Set(kits.map((k: any) => k.user_id as string)));
  const [photosRes, profilesRes, commentsRes]: any[] = await Promise.all([
    withTimeout(
      supabase.from("kit_photos").select("kit_id, url, created_at").eq("is_primary", true).in("kit_id", order).order("created_at", { ascending: false }),
      { data: [] } as any,
    ),
    withTimeout(fetchOwnerProfiles(ownerIds as string[]) as any, { data: [] } as any),
    withTimeout(supabase.from("comments").select("kit_id").in("kit_id", order), { data: [] } as any),
  ]);

  const photoMap = new Map<string, string>();
  (photosRes.data || []).forEach((p: any) => {
    if (!photoMap.has(p.kit_id)) photoMap.set(p.kit_id, p.url);
  });
  const profileMap = new Map<string, any>();
  (profilesRes.data || []).forEach((p: any) => profileMap.set(p.user_id, p));
  const commentsMap = new Map<string, number>();
  (commentsRes.data || []).forEach((c: any) => commentsMap.set(c.kit_id, (commentsMap.get(c.kit_id) || 0) + 1));

  const enriched = enrichKits(kits, photoMap, profileMap, likesMap, commentsMap, new Set<string>());
  const byId = new Map(enriched.map((k) => [k.id, k]));
  return order.map((id) => byId.get(id)).filter(Boolean) as KitWithPhoto[];
};


export function useHomeFeed() {
  const { user, loading: authLoading } = useAuth();
  const [topKits, setTopKits] = useState<KitWithPhoto[]>([]);
  const [stats, setStats] = useState({ kits: 0, forSale: 0, collection: 0, collectors: 0, clubs: 0 });

  const [feedKits, setFeedKits] = useState<KitWithPhoto[]>([]);
  const [feedPage, setFeedPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const hydratedFromCacheRef = useRef(false);
  const feedPageRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const feedOffsetRef = useRef(0);
  const feedKitsRef = useRef<KitWithPhoto[]>([]);
  useEffect(() => { feedPageRef.current = feedPage; }, [feedPage]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { feedKitsRef.current = feedKits; }, [feedKits]);

  const hydratedUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (authLoading) return;
    const currentUserId = user?.id ?? null;
    if (hydratedUserIdRef.current === currentUserId) return;
    hydratedUserIdRef.current = currentUserId;
    const cached = readFeedCache(currentUserId);
    if (cached && cached.kits.length > 0) {
      setFeedKits(cached.kits);
      setFeedPage(cached.page);
      setHasMore(cached.hasMore);
      feedOffsetRef.current = cached.offset ?? cached.kits.length;
      hydratedFromCacheRef.current = true;
    } else {
      setFeedKits([]);
      setFeedPage(0);
      setHasMore(true);
      feedOffsetRef.current = 0;
      hydratedFromCacheRef.current = false;
    }
  }, [user, authLoading]);

  const applyMyLikes = useCallback((likedKitIds: Set<string>) => {
    const updateLikes = (items: KitWithPhoto[]) =>
      items.map((item) => ({ ...item, isLikedByMe: likedKitIds.has(item.id) }));
    setTopKits((prev) => updateLikes(prev));
    setFeedKits((prev) => {
      const next = updateLikes(prev);
      writeFeedCache(user?.id ?? null, next, feedPageRef.current, hasMoreRef.current, feedOffsetRef.current);
      return next;
    });
  }, [user?.id]);

  const fetchHomeData = async () => {
    const [kitsRes, collectorsRes, kitsCountRes, kitsForSaleCountRes] = await Promise.all([
      runCritical<any>(() =>
        supabase.from("kits").select("*").eq("is_public", true).eq("for_sale", false).order("created_at", { ascending: false }).order("id", { ascending: false }).limit(100) as any,
      ),

      withTimeout(
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        { count: 0, error: new Error("collectors timeout") } as any,
      ),
      withTimeout(
        supabase.from("kits").select("*", { count: "exact", head: true }).eq("is_public", true),
        { count: 0, error: new Error("kits count timeout") } as any,
      ),
      withTimeout(
        supabase.from("kits").select("*", { count: "exact", head: true }).eq("is_public", true).eq("for_sale", true),
        { count: 0, error: new Error("kits for sale count timeout") } as any,
      ),
    ]);

    const kits = kitsRes.data || [];
    const kitIds: string[] = kits.map((k: any) => k.id);
    const ownerIds: string[] = Array.from(new Set(kits.map((k: any) => k.user_id as string)));

    const [photosRes, likesRes, profilesRes, commentsRes] = kitIds.length
      ? await Promise.all([
          withTimeout(
            supabase.from("kit_photos").select("kit_id, url, created_at").eq("is_primary", true).in("kit_id", kitIds).order("created_at", { ascending: false }),
            { data: [], error: new Error("photos timeout") } as any,
          ),
          withTimeout(
            supabase.from("kit_likes").select("kit_id").in("kit_id", kitIds),
            { data: [], error: new Error("likes timeout") } as any,
          ),
          withTimeout(
            fetchOwnerProfiles(ownerIds),
            { data: [], error: new Error("profiles timeout") } as any,
          ),
          withTimeout(
            supabase.from("comments").select("kit_id").in("kit_id", kitIds),
            { data: [], error: new Error("comments timeout") } as any,
          ),
        ])
      : ([{ data: [] }, { data: [] }, { data: [] }, { data: [] }] as any);

    const photoMap = new Map<string, string>();
    (photosRes.data || []).forEach((p: any) => {
      if (!photoMap.has(p.kit_id)) photoMap.set(p.kit_id, p.url);
    });
    const profileMap = new Map<string, any>();
    (profilesRes.data || []).forEach((p: any) => profileMap.set(p.user_id, p));
    const likesMap = new Map<string, number>();
    (likesRes.data || []).forEach((l: any) => likesMap.set(l.kit_id, (likesMap.get(l.kit_id) || 0) + 1));
    const commentsMap = new Map<string, number>();
    (commentsRes.data || []).forEach((c: any) => commentsMap.set(c.kit_id, (commentsMap.get(c.kit_id) || 0) + 1));

    const suspendedIds = new Set<string>(
      (profilesRes.data || []).filter((p: any) => p.is_suspended === true).map((p: any) => p.user_id),
    );
    const visibleKits = kits.filter((k: any) => !suspendedIds.has(k.user_id));

    let initialOffset = kits.length;
    {
      let visibleSeen = 0;
      for (let i = 0; i < kits.length; i++) {
        if (!suspendedIds.has(kits[i].user_id)) visibleSeen++;
        if (visibleSeen === PAGE_SIZE) { initialOffset = i + 1; break; }
      }
    }

    const enriched = enrichKits(visibleKits, photoMap, profileMap, likesMap, commentsMap, new Set<string>());
    const uniqueClubs = new Set(visibleKits.map((k: any) => k.club)).size;

    return {
      enriched,
      kitIds,
      suspendedIds,
      initialOffset,
      collectorsCount: collectorsRes.count || 0,
      kitsCount: kitsCountRes.count || 0,
      kitsForSaleCount: kitsForSaleCountRes.count || 0,
      uniqueClubs,
    };
  };

  const { data: homeData, isLoading: queryLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["index-home", user?.id ?? "guest"],
    queryFn: fetchHomeData,
    enabled: !authLoading,
    retry: 1,
    retryDelay: 800,
  });


  const { data: globalTopKits } = useQuery({
    queryKey: ["index-top-kits-global"],
    queryFn: fetchTopKitsGlobal,
    enabled: !authLoading,
    staleTime: 60_000,
  });

  const loading = authLoading || queryLoading;

  useEffect(() => {
    if (!homeData) return;
    const { enriched, kitIds, suspendedIds, initialOffset, collectorsCount, kitsCount, kitsForSaleCount, uniqueClubs } = homeData;

    if (!globalTopKits) {
      const sorted = [...enriched]
        .sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0))
        .filter((k) => (k.likesCount || 0) > 0);
      setTopKits(sorted.slice(0, 10));
    }


    if (user) {
      if (!hydratedFromCacheRef.current) {
        const initial = enriched.slice(0, PAGE_SIZE);
        const initialHasMore = enriched.length > PAGE_SIZE;
        setFeedKits(initial);
        setFeedPage(0);
        setHasMore(initialHasMore);
        feedOffsetRef.current = initialOffset;
        writeFeedCache(user.id, initial, 0, initialHasMore, initialOffset);
      } else {
        const enrichedById = new Map<string, KitWithPhoto>();
        enriched.forEach((k) => enrichedById.set(k.id, k));
        const enrichedCreatedAts = enriched.map((k: any) => k.created_at).filter(Boolean);
        const oldestFreshCreatedAt = enrichedCreatedAts.length
          ? enrichedCreatedAts.reduce((a, b) => (a < b ? a : b))
          : null;
        setFeedKits((prevAll) => {
          const prev = prevAll.filter((k) => !suspendedIds.has(k.user_id));
          const prevIds = new Set(prev.map((k) => k.id));
          const firstPage = enriched.slice(0, PAGE_SIZE);
          const freshNew = firstPage.filter((k) => !prevIds.has(k.id));
          const refreshed = prev
            .filter((item) => {
              const fresh = enrichedById.get(item.id);
              if (fresh) return true;
              const itemCreatedAt = (item as any).created_at;
              if (!oldestFreshCreatedAt || !itemCreatedAt) return true;
              return itemCreatedAt < oldestFreshCreatedAt;
            })
            .map((item) => {
              const fresh = enrichedById.get(item.id);
              if (!fresh) return item;
              return { ...item, ...fresh, isLikedByMe: item.isLikedByMe };
            });
          const merged = [...freshNew, ...refreshed];
          const next = merged.slice().sort((a: any, b: any) => {
            const ac = a.created_at || "";
            const bc = b.created_at || "";
            if (ac !== bc) return ac < bc ? 1 : -1;
            return (a.id || "") < (b.id || "") ? 1 : -1;
          });
          writeFeedCache(user.id, next, feedPageRef.current, hasMoreRef.current, feedOffsetRef.current);
          return next;
        });
      }

      void withTimeout(
        supabase.from("kit_likes").select("kit_id").eq("user_id", user.id).in("kit_id", kitIds.length ? kitIds : ["00000000-0000-0000-0000-000000000000"]),
        { data: [], error: new Error("my likes timeout") } as any,
      ).then(({ data: myLikesData }: any) => {
        const myLikes = new Set<string>();
        (myLikesData || []).forEach((like: any) => myLikes.add(like.kit_id));
        applyMyLikes(myLikes);
      });
    } else if (!hydratedFromCacheRef.current) {
      setFeedKits([]);
      setHasMore(false);
    }

    setStats({ kits: kitsCount, forSale: kitsForSaleCount, collection: kitsCount - kitsForSaleCount, collectors: collectorsCount, clubs: uniqueClubs });
    hydratedFromCacheRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeData, user?.id]);

  useEffect(() => {
    if (!globalTopKits || globalTopKits.length === 0) return;
    setTopKits(globalTopKits);
    if (!user) return;
    const ids = globalTopKits.map((k) => k.id);
    void withTimeout(
      supabase.from("kit_likes").select("kit_id").eq("user_id", user.id).in("kit_id", ids),
      { data: [], error: new Error("top likes timeout") } as any,
    ).then(({ data }: any) => {
      const liked = new Set<string>((data || []).map((l: any) => l.kit_id));
      setTopKits((prev) => prev.map((k) => ({ ...k, isLikedByMe: liked.has(k.id) })));
    });
  }, [globalTopKits, user?.id]);


  const loadMoreFeed = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = feedPageRef.current + 1;

    try {
      const collected: any[] = [];
      let offset = feedOffsetRef.current;
      let moreInDb = true;

      // Dobieramy kolejne paczki z bazy, aż zbierzemy PAGE_SIZE widocznych koszulek
      // (część rekordów odpada przez konta zawieszone / już wyświetlone).
      while (collected.length < PAGE_SIZE && moreInDb) {
        const { data: batch } = await supabase
          .from("kits")
          .select("*")
          .eq("is_public", true)
          .eq("for_sale", false)
          .order("created_at", { ascending: false })
          .order("id", { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1);

        const rows = batch || [];
        offset += rows.length;
        if (rows.length < PAGE_SIZE) moreInDb = false;
        if (rows.length === 0) break;

        const batchOwnerIds = [...new Set(rows.map((k: any) => k.user_id))];
        const batchProfiles = await fetchOwnerProfiles(batchOwnerIds as string[]);
        const batchSuspended = new Set<string>(
          (batchProfiles.data || []).filter((p: any) => p.is_suspended === true).map((p: any) => p.user_id),
        );
        rows
          .filter((k: any) => !batchSuspended.has(k.user_id))
          .forEach((k: any) => collected.push(k));
      }

      feedOffsetRef.current = offset;

      if (collected.length === 0) {
        hasMoreRef.current = false;
        setHasMore(false);
        writeFeedCache(user?.id ?? null, feedKitsRef.current, nextPage, false, offset);
        return;
      }

      const kits = collected.slice(0, PAGE_SIZE);
      const kitIds = kits.map((k: any) => k.id);
      const userIds = [...new Set(kits.map((k: any) => k.user_id))];

      const [photosRes, likesRes, profilesRes, commentsRes] = await Promise.all([
        supabase.from("kit_photos").select("*").eq("is_primary", true).in("kit_id", kitIds).order("created_at", { ascending: false }),
        supabase.from("kit_likes").select("kit_id").in("kit_id", kitIds),
        fetchOwnerProfiles(userIds as string[]),
        supabase.from("comments").select("kit_id").in("kit_id", kitIds),
      ]);

      const myLikes = new Set<string>();
      if (user) {
        const { data: ml } = await supabase.from("kit_likes").select("kit_id").eq("user_id", user.id).in("kit_id", kitIds);
        if (ml) ml.forEach((l: any) => myLikes.add(l.kit_id));
      }

      const photoMap = new Map<string, string>();
      (photosRes.data || []).forEach((p: any) => {
        if (!photoMap.has(p.kit_id)) photoMap.set(p.kit_id, p.url);
      });
      const profileMap = new Map<string, any>();
      (profilesRes.data || []).forEach((p: any) => profileMap.set(p.user_id, p));
      const likesMap = new Map<string, number>();
      (likesRes.data || []).forEach((l: any) => likesMap.set(l.kit_id, (likesMap.get(l.kit_id) || 0) + 1));
      const commentsMap = new Map<string, number>();
      (commentsRes.data || []).forEach((c: any) => commentsMap.set(c.kit_id, (commentsMap.get(c.kit_id) || 0) + 1));

      const newKits = enrichKits(kits, photoMap, profileMap, likesMap, commentsMap, myLikes);
      // zostawiamy do dobrania te, które nie zmieściły się w tej paczce
      const leftover = collected.length - kits.length;
      const nextOffset = offset - leftover;
      feedOffsetRef.current = nextOffset;
      const nextHasMore = collected.length > kits.length || moreInDb;

      setFeedKits((prev) => {
        const existingIds = new Set(prev.map((k) => k.id));
        const deduped = newKits.filter((k) => !existingIds.has(k.id));
        const next = [...prev, ...deduped];
        writeFeedCache(user?.id ?? null, next, nextPage, nextHasMore, nextOffset);
        return next;
      });
      feedPageRef.current = nextPage;
      hasMoreRef.current = nextHasMore;
      setFeedPage(nextPage);
      setHasMore(nextHasMore);
    } catch (e) {
      console.error("loadMoreFeed failed", e);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [user]);

  return {
    user,
    loading,
    feedKits,
    topKits,
    stats,
    hasMore,
    loadingMore,
    loadMoreFeed,
    PAGE_SIZE,
    loadError: isError,
    isRetrying: isFetching && isError,
    retry: () => { void refetch(); },
  };
}
